import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import './index.scss';
import gtm from '@/utils/gtm';
import { saveCookieForShopify, saveAICookieForShopify } from '@elem/ui-common/utils/CookieForShopify';
import getLocal from '@/app/utils/getLocal';

export interface EfProductNaverItemProps {
    key: string;
    text: string;
    url?: string;
}

export interface EfProductNaverProps {
    /**
     * theme主题
     */
    theme?: 'black' | 'white';
    /**
     * 当前处于激活态的tab的key值
     */
    activeTab?: string;
    /**
     * 产品名/标题
     */
    title?: string;
    hideBuy?: boolean;
    /**
     * buy 配置
     */
    buyItem?: EfProductNaverItemProps;
    /**
     * 点击tab时，不触发setCurTab
     */
    hideClickFnTabs?: string[];
    /**
     * tab列表
     */
    tabs?: EfProductNaverItemProps[];
    /**
     * 点击tab回调
     */
    callbackTab?: any;
    /**
     * 点击购买回调
     */
    callbackBuy?: any;
    buyNowGtm?: any;
}

/**
 * 维护: kalkin
 *
 * 产品导航 - [Buy Now]按钮支持三种情况：
 * 1. 1.管理后台产品页配置了BuyLink, 且BuyLink为当前产品详情页的锚点 => 跳转回product详情页tab中定位到锚点处(_self本页面打开)
 * 2. 2.管理后台产品页配置了BuyLink, 且BuyLink不为当前产品url所在页面 => 跳转指定url页面（_blank新页面打开）
 * 3. 3.若管理后台未配置BuyLink，则buy now按钮默认跳转至bundle所在的tab中（bundle有可能不显示在tab列表中 - jp站常见）
 */
const EfProductNaver: React.FC<EfProductNaverProps> = ({
    theme = 'white',
    activeTab = 'Product',
    title,
    hideBuy = false,
    buyItem = {
        key: 'Buy',
        text: 'Buy Now',
    },
    hideClickFnTabs = [''],
    tabs = [],
    callbackTab,
    callbackBuy,
    buyNowGtm,
}) => {
    const prefixCls = `ef-product-naver`;
    const prefix = `${prefixCls}-`;
    const classes = classNames(prefixCls, {
        [`${prefixCls}-${theme}`]: theme,
    });

    const [curTab, setCurTab] = useState(activeTab);
    const { locale = 'us' } = getLocal();

    const handleClickTab = (tab: EfProductNaverItemProps) => {
        if (callbackTab) {
            callbackTab(tab);
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_navigation',
            navigation_name: tab.text,
            product_name: title,
        });
        if (hideClickFnTabs.includes(tab.key)) {
            return;
        }
        setCurTab(tab.key);
    };

    const handleBuy = () => {
        try {
            if (buyItem?.url && buyItem?.key === 'Buy') {
                saveAICookieForShopify(buyItem?.url, locale);
            }

            if (buyNowGtm) {
                gtm.push({
                    ...buyNowGtm,
                });
            } else {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'jump_button',
                    position: 'Top',
                    button_name: buyItem.text,
                    url: buyItem?.url || 'none',
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (buyItem?.url && buyItem?.key === 'Buy') {
                window.open(
                    saveCookieForShopify(buyItem?.url, locale) || buyItem?.url,
                    buyItem.url.indexOf(window.location.pathname) > -1 ? '_self' : '_blank',
                );
            } else if (buyItem?.key !== 'Buy') {
                setCurTab(buyItem.key);
            }
            if (callbackBuy) {
                callbackBuy();
            }
        }
    };

    useEffect(() => {
        setCurTab(activeTab);
    }, [activeTab]);

    return (
        <div className={classes}>
            <div className={`${prefix}inner`}>
                <p className={`${prefix}title`}>{title}</p>
                <ul className={`${prefix}tabs`}>
                    {tabs?.map((tab: EfProductNaverItemProps, k: number) => (
                        <li className={`${prefix}tab${tab?.key === curTab ? ` ${prefix}tab-active` : ''}`} key={k} onClick={() => handleClickTab(tab)}>
                            {tab.text}
                        </li>
                    ))}
                </ul>
                {buyItem && !hideBuy && (
                    <span className={`${prefix}buy`} onClick={handleBuy}>
                        {buyItem.text}
                    </span>
                )}
            </div>
        </div>
    );
};

export default EfProductNaver;
